import React from "react";
import Input from "../Atomos/Inputs/Input";
import TextArea from "../Atomos/Inputs/TextArea";
import InputText from "../staff/InputText";
import InputTextArea from "../staff/InputTextArea";
import DatePicker from "../staff/DatePicker";
import CheckBox from "../staff/CheckBox";
import type { GuestFormSlot } from "../../utils/registrationGuestFormSlots";

type GuestFormSlotsProps = {
  slots: GuestFormSlot[];
  onSlotsChange: (slots: GuestFormSlot[]) => void;
  disabled?: boolean;
  showRegistrationDate?: boolean;
  variant?: "member" | "staff";
  guestFeeLabel?: React.ReactNode;
  showAsLabel?: boolean;
  className?: string;
};

function updateSlotAtIndex(
  slots: GuestFormSlot[],
  index: number,
  patch: Partial<GuestFormSlot>,
): GuestFormSlot[] {
  return slots.map((slot, i) => (i === index ? { ...slot, ...patch } : slot));
}

function toggleSlotEnabled(
  slots: GuestFormSlot[],
  index: number,
  enabled: boolean,
): GuestFormSlot[] {
  if (!enabled) {
    return updateSlotAtIndex(slots, index, {
      enabled: false,
      name: "",
      specialRequest: "",
    });
  }
  return updateSlotAtIndex(slots, index, { enabled: true });
}

export default function GuestFormSlots({
  slots,
  onSlotsChange,
  disabled = false,
  showRegistrationDate = false,
  variant = "member",
  guestFeeLabel,
  showAsLabel = false,
  className = "",
}: GuestFormSlotsProps): React.ReactElement {
  const isStaff = variant === "staff";

  return (
    <div className={`flex flex-col gap-6 w-full ${className}`}>
      {slots.map((slot, index) => {
        const fieldsDisabled = disabled || !slot.enabled;
        const showFieldsAsLabel = showAsLabel && disabled;
        const slotKey = slot.id ?? `guest-slot-${index}`;

        return (
          <div
            key={slotKey}
            className={
              isStaff ? "flex flex-col gap-4" : "flex flex-col gap-4 w-full"
            }
          >
            <div
              className={
                isStaff
                  ? "flex flex-wrap items-center gap-3"
                  : "flex flex-wrap items-center justify-between gap-3 w-full"
              }
            >
              {!disabled &&
                (isStaff ? (
                  <CheckBox
                    checked={slot.enabled}
                    disabled={disabled}
                    onCheckedChange={(checked) => {
                      onSlotsChange(toggleSlotEnabled(slots, index, checked));
                    }}
                    label="Add Guest"
                    labelClassName="font-semibold"
                  />
                ) : (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={slot.enabled}
                      disabled={disabled}
                      onChange={(e) => {
                        onSlotsChange(
                          toggleSlotEnabled(slots, index, e.target.checked),
                        );
                      }}
                      className="w-4 h-4 accent-primary cursor-pointer"
                    />
                    <span className="text-base md:text-lg font-semibold text-gray-font">
                      Add Guest
                    </span>
                  </label>
                ))}
              {index === 0 && guestFeeLabel}
            </div>

            <div
              className={
                isStaff
                  ? "grid grid-cols-10 gap-4"
                  : "grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,2.5fr)] gap-5 w-full"
              }
            >
              {showRegistrationDate && (
                <DatePicker
                  title="Registration Date"
                  value={slot.registrationDate ?? null}
                  onChange={(date) => {
                    onSlotsChange(
                      updateSlotAtIndex(slots, index, {
                        registrationDate: date
                          ? Math.floor(date.getTime() / 1000)
                          : Math.floor(Date.now() / 1000),
                      }),
                    );
                  }}
                  disabled={fieldsDisabled}
                  showAsLabel={showFieldsAsLabel}
                  className="col-span-10 md:col-span-5 2xl:col-span-3"
                />
              )}
              {isStaff ? (
                <>
                  <InputText
                    title="Guest Name"
                    required
                    value={slot.name}
                    onChange={(value) => {
                      onSlotsChange(
                        updateSlotAtIndex(slots, index, { name: value }),
                      );
                    }}
                    disabled={fieldsDisabled}
                    showAsLabel={showFieldsAsLabel}
                    className="col-span-10 md:col-span-5 2xl:col-span-3"
                  />
                  <InputTextArea
                    title="Special Requests"
                    value={slot.specialRequest}
                    onChange={(value) => {
                      onSlotsChange(
                        updateSlotAtIndex(slots, index, {
                          specialRequest: value,
                        }),
                      );
                    }}
                    disabled={fieldsDisabled}
                    showAsLabel={showFieldsAsLabel}
                    maxChars={250}
                    showCharacterCount
                    className="col-span-10 2xl:col-span-4"
                  />
                </>
              ) : (
                <>
                  <div className="min-w-0 w-full">
                    <Input
                      title="Guest Name"
                      name={`guestName-${index}`}
                      value={slot.name}
                      disabled={fieldsDisabled}
                      onChange={(e) => {
                        onSlotsChange(
                          updateSlotAtIndex(slots, index, {
                            name: e.target.value,
                          }),
                        );
                      }}
                      placeholder=""
                      error={false}
                      required={false}
                      className={fieldsDisabled ? "" : "!bg-white"}
                    />
                  </div>
                  <div className="min-w-0 w-full">
                    <TextArea
                      title="Special Requests"
                      name={`guestSpecialRequest-${index}`}
                      value={slot.specialRequest}
                      disabled={fieldsDisabled}
                      onChange={(e) => {
                        onSlotsChange(
                          updateSlotAtIndex(slots, index, {
                            specialRequest: e.target.value,
                          }),
                        );
                      }}
                      placeholder=""
                      error={false}
                      required={false}
                      maxChars={250}
                      characterCount={true}
                      height="h-[3.5rem]"
                      className={fieldsDisabled ? "" : "bg-white"}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
